<template>
    <Teleport to="body" v-if="documentReady">
        <div v-if="visible" class="reseller-terms-modal">
            <div class="modal-overlay active" aria-hidden="true" />

            <div
                class="modal reseller-terms-modal__panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="reseller-agreement-title"
            >
                <div class="modal__header">
                    <h2 id="reseller-agreement-title" class="headline-s">Reseller Agreement Required</h2>
                </div>

                <div class="modal__body flex-column">
                    <div class="reseller-terms-modal__intro">
                        <PdfIcon class="reseller-terms-modal__icon" alt="" aria-hidden="true" />
                        <div class="reseller-terms-modal__copy">
                            <p class="paragraph-default">
                                Rothco has updated its Reseller Agreement. Your company must review and accept the
                                updated terms before continuing to use dealer account features.
                            </p>
                            <p class="paragraph-details">
                                <a :href="agreementUrl" target="_blank" rel="noopener noreferrer">Review agreement (English)</a>
                                ·
                                <a :href="agreementUrlSpanish" target="_blank" rel="noopener noreferrer">Revisar acuerdo (Español)</a>
                            </p>
                        </div>
                    </div>

                    <div v-if="canAccept" class="notification notification-informational">
                        <p class="paragraph-details">
                            As the account administrator, you can accept on behalf of your company.
                        </p>
                    </div>

                    <div v-else class="notification notification-warning">
                        <p class="paragraph-details">
                            Only an account administrator can accept this agreement. Please ask your company's admin to
                            log in and accept, or contact
                            <a href="mailto:CustomerService@rothco.com">CustomerService@rothco.com</a>
                            if you need help.
                        </p>
                    </div>

                    <div v-if="canAccept" class="reseller-terms-modal__confirm">
                        <label class="reseller-terms-modal__checkbox">
                            <input v-model="hasReviewedAgreement" type="checkbox" />
                            <span class="paragraph-default">
                                I have reviewed the Reseller Agreement and accept it on behalf of my company.
                            </span>
                        </label>
                    </div>

                    <div v-if="!showLater" class="notification notification-error">
                        <p class="paragraph-details">
                            Your company has reached the maximum number of reminders. Please accept the agreement to
                            continue using your dealer account.
                        </p>
                    </div>

                    <p v-else-if="dismissRemaining > 0" class="paragraph-details reseller-terms-modal__reminder">
                        You can remind yourself later {{ dismissRemaining }}
                        {{ dismissRemaining === 1 ? 'time' : 'times' }} before acceptance is required.
                    </p>
                </div>

                <div class="reseller-terms-modal__footer">
                    <RothcoCta
                        v-if="showLater"
                        cta-type="button"
                        class="secondary"
                        :disabled="processing"
                        @click="dismiss"
                    >
                        Remind Me Later
                    </RothcoCta>

                    <RothcoCta
                        v-else
                        class="secondary"
                        :href="route('home')"
                    >
                        Return to Homepage
                    </RothcoCta>

                    <RothcoCta
                        v-if="canAccept"
                        cta-type="button"
                        :class="acceptEnabled ? 'primary' : 'disabled'"
                        :disabled="!acceptEnabled"
                        @click="accept"
                    >
                        Accept Agreement
                    </RothcoCta>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import { route } from "ziggy-js";
import { useFetch } from "@/helpers/composables/useFetch";
import RothcoCta from "@/components/RothcoCta.vue";
import PdfIcon from "@icons/pdf-icon.svg";

type ResellerTermsState = {
    show: boolean;
    showLater: boolean;
    canAccept: boolean;
    agreementUrl: string;
    dismissRemaining?: number;
};

const SNOOZE_KEY = "reseller_terms_snoozed";

const page = usePage();

const termsState = ref<ResellerTermsState | null>(null);
const processing = ref(false);
const hasReviewedAgreement = ref(false);

// Teleport targets are only hydratable client-side: SSR discards teleported
// content, leaving anchors the client cannot reconcile.
const documentReady = ref(false);

onMounted(() => {
    documentReady.value = !!document.body;
});

function isSnoozed() {
    return typeof window !== "undefined" && sessionStorage.getItem(SNOOZE_KEY) === "1";
}

function syncTermsState() {
    const terms = page.props.resellerTerms as ResellerTermsState | null;

    if (!terms?.show || isSnoozed()) {
        termsState.value = null;
        return;
    }

    termsState.value = terms;
    hasReviewedAgreement.value = false;
}

syncTermsState();

watch(() => page.props.resellerTerms, syncTermsState);
watch(() => page.url, syncTermsState);

const removeNavigationListener = router.on("success", () => {
    sessionStorage.removeItem(SNOOZE_KEY);
    syncTermsState();
});

onUnmounted(() => {
    removeNavigationListener();
});

const visible = computed(() => Boolean(termsState.value?.show));
const showLater = computed(() => Boolean(termsState.value?.showLater));
const canAccept = computed(() => Boolean(termsState.value?.canAccept));
const acceptEnabled = computed(() => !processing.value && hasReviewedAgreement.value);
const dismissRemaining = computed(() => termsState.value?.dismissRemaining ?? 0);
const agreementUrl = computed(() => termsState.value?.agreementUrl ?? route("index.resellerAgreement"));
const agreementUrlSpanish = computed(() => route("index.resellerAgreement", ["es"]));

const apiFetchOptions = {
    immediate: false,
    beforeFetch(ctx: { options: RequestInit }) {
        ctx.options.headers = {
            ...ctx.options.headers,
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
        };

        return ctx;
    },
};

async function postResellerTerms(endpoint: string) {
    const request = useFetch(endpoint, apiFetchOptions).post().json();

    await request.execute();

    const status = request.statusCode.value ?? 0;

    return {
        ok: !request.error.value && status >= 200 && status < 300,
        request,
    };
}

async function accept() {
    if (!acceptEnabled.value) {
        return;
    }

    processing.value = true;

    try {
        const { ok } = await postResellerTerms(route("endpoint.reseller_terms.accept"));

        if (ok) {
            termsState.value = null;
            router.reload({ only: ["resellerTerms"], preserveScroll: true });
        }
    } finally {
        processing.value = false;
    }
}

async function dismiss() {
    if (processing.value) {
        return;
    }

    processing.value = true;
    sessionStorage.setItem(SNOOZE_KEY, "1");
    termsState.value = null;
    hasReviewedAgreement.value = false;

    try {
        const { ok } = await postResellerTerms(route("endpoint.reseller_terms.dismiss"));

        if (!ok) {
            sessionStorage.removeItem(SNOOZE_KEY);
            syncTermsState();
        }
    } finally {
        processing.value = false;
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.reseller-terms-modal {
    position: fixed;
    inset: 0;
    z-index: 10000;

    .modal-overlay {
        z-index: 0;
    }

    .reseller-terms-modal__panel {
        position: fixed;
        z-index: 1;
    }
}

.reseller-terms-modal__panel {
    max-width: 42rem;
}

.reseller-terms-modal__intro {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
}

.reseller-terms-modal__icon {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
}

.reseller-terms-modal__copy {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    a {
        color: $rothcoRed;
        text-decoration: underline;
    }
}

.reseller-terms-modal__confirm {
    margin-top: 0.25rem;
}

.reseller-terms-modal__checkbox {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    cursor: pointer;

    input {
        margin-top: 0.2rem;
        flex-shrink: 0;
    }
}

.reseller-terms-modal__reminder {
    color: $rothcoDarkGrey;
    margin: 0;
}

.reseller-terms-modal__footer {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 0.063rem solid $rothcoBorderGrey;
}

.flex-column {
    flex-direction: column;
    gap: 1rem;
}
</style>
